/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type RefObject } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';

import { ConnectAppsSearchIcon } from './connect-apps-icon';
import { LegacySearchIcon } from './legacy-search-icon';
import { messages } from './messages';

const textFieldBoxBaseStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: 'size.300',
	':focus': {
		height: 'size.400',
	},
	width: '100%',
	paddingRight: 'space.200',
});

const textFieldBoxStyles = xcss({
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderColor: 'color.border',
	backgroundColor: 'elevation.surface.overlay',
	borderRadius: token('radius.small'),
});

const textFieldBoxStylesT26Shape = xcss({
	borderRadius: 'radius.medium',
});

const textFieldBoxFocusedStyles = xcss({
	height: 'size.400',
	border: 'none',
	paddingRight: '0',
});

const textFieldBoxFocusedElevationStyles = xcss({
	boxShadow: 'elevation.shadow.overlay',
});

const textFieldInDialogStyles = xcss({
	marginTop: 'space.075',
	position: 'relative',
	zIndex: 'layer',
});

const inputStyles = css({
	outline: 'none',
	background: 'none',
	font: token('font.body'),
	color: token('color.text.subtlest'),
	border: 'none',
	width: '100%',
	padding: '0',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',

	'&::placeholder': {
		color: token('color.text.subtlest'),
	},
});

const inputFocusedStyles = css({
	color: token('color.text'),
});

const iconWrapperStyles = xcss({
	minWidth: 'size.300',
	minHeight: 'size.300',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	color: 'color.text.subtlest',
});

const iconWrapperFocusedStyles = xcss({
	marginInlineStart: 'space.200',
	marginInlineEnd: 'space.150',
	minWidth: 'size.200',
});

const clearIconFadeInAnimation = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const clearIconFadeInWrapperStyles = css({
	marginRight: token('space.100', '8px'),
	animation: `${clearIconFadeInAnimation} 150ms ease-out`,
	animationFillMode: 'forwards',
});

// Styles for the trailing Connect Apps icon slot.
const connectAppsIconStyles = xcss({
	marginRight: 'space.050',
	display: 'flex',
	alignItems: 'center',
});

// When QuickFind is open, align the pill with where the close icon sits.
const connectAppsIconOpenStyles = xcss({
	marginRight: 'space.100',
});

const textFieldBoxNoRightPaddingStyles = xcss({
	paddingRight: 'space.0',
});

// Nav3 style overrides
// Can be removed once all supported products use Nav4
const nav3StyleOverrides = {
	textFieldBoxStyles: xcss({
		borderColor: 'color.border.input',
		backgroundColor: 'color.background.input',
		borderRadius: 'radius.medium',
		width: '200px',
	}),
	textFieldBoxFocusedStyles: xcss({
		backgroundColor: 'elevation.surface.overlay',
		borderRadius: 'radius.large',
		width: '780px',
	}),
	iconWrapperStyles: xcss({
		minWidth: '30px',
	}),
	inputStyles: css({
		font: token('font.body'),
	}),
};

export type TextfieldProps = {
	value?: string;
	dialogId: string;
	quickFindOpen: boolean;
	isNav4Enabled?: boolean;
	isAdminHubAIEnabled?: boolean;
	intentRouterEnabled?: boolean;
	activeDescendantId?: string;
	onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	onFocus?: React.FocusEventHandler<HTMLInputElement>;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	onSelect?: React.ReactEventHandler<HTMLInputElement>;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	onClick?: React.MouseEventHandler<HTMLInputElement>;
	onClear?: () => void;
	/**
	 * Called when the user clicks the connect-apps pill button. Forwarded to
	 * `<ConnectAppsSearchIcon>`'s built-in `onAppsClick` prop.
	 */
	onAppsClick?: () => void;
	inputRef?: RefObject<HTMLInputElement>;
	clearIconRef?: RefObject<HTMLButtonElement>;
	placeholder?: string;
	cloudId?: string;
	primaryProduct?: string;
	smartLinkUsage?: Record<string, number>;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
};

export const Textfield: ({
	value,
	dialogId,
	quickFindOpen,
	isNav4Enabled,
	onEnter,
	onFocus,
	onBlur,
	onSelect,
	onKeyDown,
	onChange,
	onClick,
	onClear,
	onAppsClick,
	inputRef,
	clearIconRef,
	placeholder,
	intentRouterEnabled,
	cloudId,
	primaryProduct,
}: TextfieldProps) => JSX.Element = ({
	value,
	dialogId,
	quickFindOpen,
	isNav4Enabled,
	onEnter,
	onFocus,
	onBlur,
	onSelect,
	onKeyDown,
	onChange,
	onClick,
	onClear,
	onAppsClick,
	inputRef,
	clearIconRef,
	placeholder,
	intentRouterEnabled,
	activeDescendantId,
	cloudId,
	primaryProduct,
	smartLinkUsage,
	thirdPartyConfigs,
}: TextfieldProps) => {
	const intl = useIntl();

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!e.defaultPrevented && e.key === 'Enter') {
			e.preventDefault();
			onEnter && onEnter(e);
		}
		if (typeof onKeyDown === 'function') {
			onKeyDown(e);
		}
	};

	const clearIconButton = (
		<IconButton
			ref={clearIconRef}
			icon={(iconProps) => <CrossIcon {...iconProps} color={token('color.icon')} label="" />}
			spacing={'compact'}
			onClick={onClear}
			label={intl.formatMessage(messages.clearIconLabel)}
			appearance="subtle"
			testId="search-input-clear-button"
		/>
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={[
				textFieldBoxBaseStyles,
				(!intentRouterEnabled || !quickFindOpen) && textFieldBoxStyles,
				intentRouterEnabled && quickFindOpen && textFieldInDialogStyles,
				fg('platform-dst-shape-theme-default') && textFieldBoxStylesT26Shape,
				quickFindOpen ? textFieldBoxFocusedStyles : false,
				!intentRouterEnabled && quickFindOpen && textFieldBoxFocusedElevationStyles,
				!isNav4Enabled && nav3StyleOverrides.textFieldBoxStyles,
				!isNav4Enabled && quickFindOpen && nav3StyleOverrides.textFieldBoxFocusedStyles,
				textFieldBoxNoRightPaddingStyles,
			]}
			onClick={(e: React.MouseEvent<HTMLDivElement>) => {
				// React's synthetic events bubble through the JSX tree, not the DOM. The Rovo
				// Settings modal is a portal, so clicks inside it bubble to this onClick even
				// though they're outside this DOM subtree. Guard against that here, otherwise
				// closing the modal re-focuses the input and reopens Quick Find.
				if (!e.currentTarget.contains(e.target as HTMLElement)) {
					return;
				}
				inputRef?.current?.click();
				inputRef?.current?.focus();
			}}
			testId="search-input-container"
			role="search"
		>
			<Box
				xcss={[
					iconWrapperStyles,
					quickFindOpen && iconWrapperFocusedStyles,
					!isNav4Enabled && nav3StyleOverrides.iconWrapperStyles,
				]}
			>
				{isNav4Enabled || quickFindOpen ? (
					<SearchIcon color="currentColor" label="" aria-hidden="true" />
				) : (
					<LegacySearchIcon isExpanded={!!quickFindOpen} />
				)}
			</Box>
			<input
				ref={inputRef}
				css={[
					inputStyles,
					quickFindOpen && inputFocusedStyles,
					!isNav4Enabled && nav3StyleOverrides.inputStyles,
				]}
				onFocus={onFocus}
				onBlur={onBlur}
				onSelect={onSelect}
				onKeyDown={handleKeyDown}
				onClick={onClick}
				placeholder={placeholder || intl.formatMessage(messages.inputPlaceholder)}
				onChange={onChange}
				value={value}
				data-testid="search-input"
				autoComplete="off"
				aria-label={intl.formatMessage(messages.inputAriaLabel)}
				aria-expanded={quickFindOpen}
				aria-controls={dialogId}
				aria-activedescendant={activeDescendantId ?? ''}
				aria-haspopup="true"
				role="combobox"
			/>
			{quickFindOpen && value && <div css={clearIconFadeInWrapperStyles}>{clearIconButton}</div>}
			<Box xcss={[connectAppsIconStyles, quickFindOpen && connectAppsIconOpenStyles]}>
				<ConnectAppsSearchIcon
					start={quickFindOpen && !!thirdPartyConfigs}
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					smartLinkUsage={smartLinkUsage}
					thirdPartyConfigs={thirdPartyConfigs}
					onAppsClick={onAppsClick}
				/>
			</Box>
		</Box>
	);
};
